import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { FeedbackRequest, FeedbackQuestion } from '../types/feedback';
import FilterBar from './FilterBar';
import RequestsTable from './RequestsTable';
import { Toast, ConfirmDialog } from './Toast';
import { UserProfile } from '../types/userProfile';
import { useApiClient } from '../hooks/useApiClient';
import { useToast } from '../hooks/useToast';
import { mapApiFeedbackRequestsToList, mapApiFeedbackRequestFull } from '../utils/mappers';
import { generateConnectPDFContent, printHtmlInNewWindow } from '../utils/pdfTemplates';

interface MyFeedbackViewProps {
  userProfile: UserProfile | null;
  onRequestCountChange?: (count: number) => void;
}

export function MyFeedbackView({ userProfile, onRequestCountChange }: MyFeedbackViewProps) {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<FeedbackRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [questions, setQuestions] = useState<FeedbackQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  
  // Filters
  const [filterYear, setFilterYear] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const { apiCall } = useApiClient();
  const { toasts, hideToast, success, error: showError } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<FeedbackRequest | null>(null);
  
  // Store apiCall in ref to avoid dependency issues
  const apiCallRef = useRef(apiCall);
  apiCallRef.current = apiCall;

  // Fetch feedback requests from backend
  useEffect(() => {
    const fetchRequests = async () => {
      if (!userProfile?.employeeId) {
        setRequestsLoading(false);
        return;
      }
      setRequestsLoading(true);
      try {
        const response = await apiCallRef.current(
          `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/my-feedback/${encodeURIComponent(userProfile.employeeId)}`
        );
        if (response.ok) {
          const result = await response.json();
          if (result.data) {
            const mappedRequests = mapApiFeedbackRequestsToList(result.data);
            setRequests(mappedRequests);
            onRequestCountChange?.(mappedRequests.length);
          }
        } else {
          console.error('Failed to fetch requests');
        }
      } catch (error) {
        console.error('Error fetching requests:', error);
      } finally {
        setRequestsLoading(false);
      }
    };

    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.employeeId]);
  
  // Fetch questions from backend when opening the form
  const fetchQuestions = async (): Promise<FeedbackQuestion[]> => {
    setQuestionsLoading(true);
    try {
      // Use manager-specific endpoint if manager exists, otherwise get default questions
      const endpoint = userProfile?.manager?.employeeId
        ? `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackQuestions/for-employee/${encodeURIComponent(userProfile.manager.employeeId)}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackQuestions`;
      
      const response = await apiCall(endpoint);
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          const sortedQuestions = result.data
            .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
            .map((q: any) => ({
              id: q.id,
              question: q.question,
              placeholder: q.placeholder || ''
            }));
          setQuestions(sortedQuestions);
          return sortedQuestions;
        }
      } else {
        console.error('Failed to fetch questions');
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setQuestionsLoading(false);
    }
    return [];
  };

  // Memoized filtered requests
  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      const yearMatch = filterYear === 'all' || request.year === parseInt(filterYear);
      const searchMatch = !searchQuery || 
        (request.connectName && request.connectName.toLowerCase().includes(searchQuery.toLowerCase()));
      return yearMatch && searchMatch;
    });
  }, [requests, filterYear, searchQuery]);

  const handleNewRequest = async () => {
    const fetchedQuestions = await fetchQuestions();
    // Navigate directly to the new connect page — name is editable inline
    navigate('/new-feedback-request', {
      state: {
        questions: fetchedQuestions
      }
    });
  };

  const handleViewRequest = async (request: FeedbackRequest) => {
    // Navigate to the view page instead of opening a modal
    navigate(`/view-feedback-request/${request.id}`, {
      state: { request }
    });
  };

  // Handle edit request - open full page in edit mode
  const handleEditRequest = async (request: FeedbackRequest) => {
    navigate(`/view-feedback-request/${request.id}`, {
      state: { request, startEditing: true }
    });
  };

  const handleExportPDF = async (request: FeedbackRequest) => {
    if (request.status !== 'completed') return;
    
    try {
      // Fetch full request details
      const response = await apiCallRef.current(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${request.id}`
      );
      if (!response.ok) {
        console.error('Failed to fetch request details');
        return;
      }
      const result = await response.json();
      const fullRequest = mapApiFeedbackRequestFull(result.data);

      // Fetch core priorities
      let corePriorities = null;
      try {
        const prioritiesResponse = await apiCallRef.current(
          `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities/employee/${encodeURIComponent(request.employeeId)}/year/${request.year}`
        );
        if (prioritiesResponse.ok) {
          const prioritiesResult = await prioritiesResponse.json();
          if (prioritiesResult.data) {
            corePriorities = prioritiesResult.data;
          }
        }
      } catch (err) {
        console.log('No core priorities found');
      }

      const pdfContent = generateConnectPDFContent(fullRequest, corePriorities);
      printHtmlInNewWindow(pdfContent);
    } catch (err) {
      console.error('Error exporting PDF:', err);
    }
  };

  const handleDeleteRequest = (request: FeedbackRequest) => {
    setDeleteTarget(request);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await apiCallRef.current(
        `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/${deleteTarget.id}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        setRequests(prev => prev.filter(r => r.id !== deleteTarget.id));
        success('Connect request deleted successfully');
      } else {
        showError('Failed to delete connect request');
      }
    } catch (err) {
      console.error('Error deleting request:', err);
      showError('Error deleting connect request');
    } finally {
      setDeleteTarget(null);
    }
  };

  if (requestsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-pink-500 animate-spin" />
          <p className="text-slate-600 font-medium">Loading your feedback requests...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <FilterBar
        filterYear={filterYear}
        onYearChange={setFilterYear}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        resultCount={filteredRequests.length}
        onNewRequest={handleNewRequest}
      />

      <RequestsTable
        requests={filteredRequests}
        onViewRequest={handleViewRequest}
        onEditRequest={handleEditRequest}
        onExportPDF={handleExportPDF}
        onDeleteRequest={handleDeleteRequest}
      />

      {questionsLoading && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">Loading...</p>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <ConfirmDialog
          title="Delete Connect Request"
          message={`Are you sure you want to delete "${deleteTarget.connectName || 'this connect'}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => hideToast(toast.id)}
        />
      ))}

    </>
  );
}
